/**
 * /sso-callback — intermediate landing page for OAuth (and SAML / OIDC)
 * sign-in redirects.
 *
 * When the user clicks "Continue with Google" on /sign-in, Clerk's
 * `signIn.authenticateWithRedirect({ redirectUrl })` sends the
 * browser to the provider, then back to this path. We call
 * `clerk.handleRedirectCallback()` directly (rather than mounting
 * `<AuthenticateWithRedirectCallback />`) so we can catch the
 * **restricted-mode rejection** case — where the Clerk instance has
 * sign-ups disabled and the OAuth round-trip succeeds but no session
 * is minted. The default component just silently falls back to the
 * home URL in that case, which leaves the user staring at /sign-in
 * with no explanation. We surface a clear banner instead by routing
 * to /sign-in?error=access_denied.
 *
 * `signInFallbackRedirectUrl` / `signUpFallbackRedirectUrl` cover
 * the new-user case: if the OAuth account has never signed in
 * before AND restricted mode is OFF, Clerk transitions to a
 * sign-up automatically and lands the user on `/onboarding`.
 */

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useClerk } from '@clerk/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/_auth/sso-callback')({
  component: SsoCallbackPage,
  validateSearch: (search): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});

interface ClerkError {
  code?: string;
  message?: string;
  longMessage?: string;
}

function SsoCallbackPage(): ReactElement {
  const { redirect } = Route.useSearch();
  const clerk = useClerk();
  const navigate = useNavigate();
  const fired = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clerk || fired.current) return;
    fired.current = true;

    void (async () => {
      try {
        await clerk.handleRedirectCallback({
          signInFallbackRedirectUrl: redirect ?? '/',
          signUpFallbackRedirectUrl: '/onboarding',
        });
        // handleRedirectCallback navigates on success; nothing more
        // to do here. If we *don't* navigate and the user is still
        // signed-out 1.5s later, treat that as the silent
        // restricted-mode failure (Clerk doesn't always throw — it
        // can resolve with no session attached).
        setTimeout(() => {
          if (!clerk.session) {
            navigate({
              to: '/sign-in',
              search: { error: 'access_denied', redirect },
              replace: true,
            });
          }
        }, 1500);
      } catch (err) {
        const e = err as { errors?: ClerkError[]; message?: string };
        const first = e.errors?.[0];
        const code = first?.code;
        const msg = first?.longMessage ?? first?.message ?? e.message ?? '';
        // Restricted-mode / signup-blocked indicators across Clerk versions.
        if (
          code === 'access_denied' ||
          code === 'signup_disabled' ||
          code === 'restricted_signup' ||
          code === 'form_identifier_not_found' ||
          /restricted|invitation|not allowed to sign/i.test(msg)
        ) {
          navigate({
            to: '/sign-in',
            search: { error: 'access_denied', redirect },
            replace: true,
          });
          return;
        }
        setError(msg || 'Sign-in failed. Try again.');
      }
    })();
  }, [clerk, redirect, navigate]);

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 p-6 text-center">
        <p className="text-sm text-[var(--color-danger)]">{error}</p>
        <Link to="/sign-in" search={{ redirect }}>
          <Button variant="outline">Back to sign in</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
      <Spinner className="size-5 text-[var(--color-mute)]" />
      <p className="text-sm text-[var(--color-mute)]">Finishing sign-in…</p>
    </div>
  );
}
